import React from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Snackbar
} from '@mui/material';
import apiClient from '../../services/api';

const AdditionalInfoForm = ({ initialData = {}, onSave, onCancel, loading = false }) => {
  const [formData, setFormData] = React.useState({ ...initialData });
  const [errors, setErrors] = React.useState({});
  const [saveError, setSaveError] = React.useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const [isDataModified, setIsDataModified] = React.useState(false);

  // 监听initialData变化，更新表单数据
  React.useEffect(() => {
    if (!isDataModified) {
      setFormData({ ...initialData });
    }
  }, [initialData, isDataModified]);

  // 监听loading状态，触发保存
  React.useEffect(() => {
    if (loading) {
      setTimeout(() => {
        handleSave();
      }, 100);
    }
  }, [loading]);

  // 处理表单字段变化
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 清除对应字段的错误
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    setIsDataModified(true);
  };

  // 验证表单
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.preferredStartDate) {
      newErrors.preferredStartDate = '请选择期望入学时间';
    } else {
      // 验证年月格式和未来时间
      const selectedDate = new Date(formData.preferredStartDate + '-01');
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();
      
      if (selectedDate.getFullYear() < currentYear || 
          (selectedDate.getFullYear() === currentYear && selectedDate.getMonth() <= currentMonth)) {
        newErrors.preferredStartDate = '期望入学时间必须是未来的年月';
      }
    }
    if (!formData.studyBudget) {
      newErrors.studyBudget = '请选择学习预算';
    }
    if (!formData.accommodationNeeds) {
      newErrors.accommodationNeeds = '请选择住宿需求';
    }
    if (!formData.visaHistory) {
      newErrors.visaHistory = '请选择签证记录';
    }
    
    setErrors(newErrors);
    
    // 如果有错误，显示错误信息
    if (Object.keys(newErrors).length > 0) {
      setSaveError('请填写所有必填字段');
      setShowErrorSnackbar(true);
    }
    
    return Object.keys(newErrors).length === 0;
  };

  // 处理保存
  const handleSave = async () => {
    setSaveError('');
    
    if (!validateForm()) {
      onSave && onSave(null);
      setShowErrorSnackbar(true);
      return;
    }

    try {
      const response = await apiClient.post('/api/application/update-additional-info', {
        additionalInformation: formData
      });
      
      if (response && response.success) {
        setIsDataModified(false);
        onSave && onSave(formData);
      } else {
        setSaveError(response?.message || '保存失败');
        onSave && onSave(null);
        setShowErrorSnackbar(true);
      }
    } catch (error) {
      console.error('保存其他信息失败:', error);
      setSaveError(error?.response?.data?.message || error.message || '保存失败');
      onSave && onSave(null);
      setShowErrorSnackbar(true);
    }
  };

  return (
    <Box>
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        message={saveError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
        其他信息
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="期望入学时间 *"
            type="month"
            value={formData.preferredStartDate || ''}
            onChange={(e) => handleFormChange('preferredStartDate', e.target.value)}
            error={!!errors.preferredStartDate}
            helperText={errors.preferredStartDate}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              pattern: '[0-9]{4}-[0-9]{2}',
              placeholder: 'YYYY-MM'
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors.studyBudget}>
            <InputLabel>学习预算 *</InputLabel>
            <Select
              value={formData.studyBudget || ''}
              onChange={(e) => handleFormChange('studyBudget', e.target.value)}
              label="学习预算 *"
            >
              <MenuItem value="50k">5万以下</MenuItem>
              <MenuItem value="50k-100k">5-10万</MenuItem>
              <MenuItem value="100k-200k">10-20万</MenuItem>
              <MenuItem value="200k">20万以上</MenuItem>
            </Select>
            {errors.studyBudget && (
              <FormHelperText>{errors.studyBudget}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors.accommodationNeeds}>
            <InputLabel>住宿需求 *</InputLabel>
            <Select
              value={formData.accommodationNeeds || ''}
              onChange={(e) => handleFormChange('accommodationNeeds', e.target.value)}
              label="住宿需求 *"
            >
              <MenuItem value="dormitory">学校宿舍</MenuItem>
              <MenuItem value="homestay">寄宿家庭</MenuItem>
              <MenuItem value="apartment">独立公寓</MenuItem>
              <MenuItem value="no_need">不需要</MenuItem>
            </Select>
            {errors.accommodationNeeds && (
              <FormHelperText>{errors.accommodationNeeds}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors.visaHistory}>
            <InputLabel>签证记录 *</InputLabel>
            <Select
              value={formData.visaHistory || ''}
              onChange={(e) => handleFormChange('visaHistory', e.target.value)}
              label="签证记录 *"
            >
              <MenuItem value="none">无</MenuItem>
              <MenuItem value="approved">有，已获签</MenuItem>
              <MenuItem value="rejected">有，被拒签</MenuItem>
            </Select>
            {errors.visaHistory && (
              <FormHelperText>{errors.visaHistory}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="工作经历说明"
            multiline
            rows={4}
            value={formData.workExperience || ''}
            onChange={(e) => handleFormChange('workExperience', e.target.value)}
            placeholder="请详细描述您的工作经历，包括工作职责、成就等..."
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="学习动机"
            multiline
            rows={4}
            value={formData.motivation || ''}
            onChange={(e) => handleFormChange('motivation', e.target.value)}
            placeholder="请说明您选择出国留学的动机和目标..."
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="职业目标"
            multiline
            rows={4}
            value={formData.careerGoals || ''}
            onChange={(e) => handleFormChange('careerGoals', e.target.value)}
            placeholder="请描述您的职业规划和目标..."
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="补充信息"
            multiline
            rows={4}
            value={formData.additionalInfo || ''}
            onChange={(e) => handleFormChange('additionalInfo', e.target.value)}
            placeholder="如有其他需要补充的信息，请在此填写..."
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdditionalInfoForm; 